/**
 * @file purchase-order-details.tsx
 * @deprecated Copied to app/modules/procurement/pages/po-management/purchase-order-details.tsx. This file still serves the existing /UProcure routes.
 * @module app/modules/uProcure/po-management/purchase-order-details
 * @description Enhanced purchase order detail view with improved UI/UX for displaying
 *   comprehensive PO information, vendor details, ordered items with various quantity
 *   types, shipment status timeline, and integrated escalation management. Supports
 *   permission-based action visibility and manages goods request timeline display
 *   alongside PO lifecycle status transitions.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 *
 * @todo [P0] Align navigation paths with mounted uTrade route prefix for PO module
 *   (edit/cancel currently navigate to /purchase-order-management/* instead of
 *   /UProcure/purchase-order-management/*).
 * @todo [P0] Replace simulated cancel flow in handleCancel with backend cancel
 *   API integration and preserve adminNote plus auditLogMessage payload end-to-end.
 * @todo [P1] Include auditLogMessage when submitting escalation actions to keep
 *   uTrade parity with the source uProcure workflow.
 * @todo [P1] Make the cancel confirmation modal title dynamic so it reflects the
 *   current PO/vendor instead of a hardcoded company name.
 * @todo [P2] Consolidate duplicated delivery toggle handlers (handleGrnToggle and
 *   handlePartialDeliveryToggle) into one reusable handler.
 * @todo [P2] Remove or re-enable dead code paths:
 *   escalations fetch + commented escalation UI + three-dot menu handlers/imports.
 * @todo [P2] Review loader state ownership to avoid double toggling initLoader
 *   across fetchPurchaseOrderInfo and its wrapping effect.
 * @todo [P3] Reduce any/unused declarations (types, imports, helpers) to improve
 *   maintainability and eliminate lint noise.
 */

import { Fragment, useEffect, useRef, useState } from "react";
import { Button, Card, Col, Form, Modal, Row } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import PoTimelinePanel from "./po-timeline-panel";
import threeDot from "../../../../_admin/assets/media/svg_uTrade/three-dot-round.svg";
import Loader from "../../../../Global/loader";
import {
  Admin,
  BulkShipmentSubmitted,
  AllModules,
  CartonWithDozens,
  CartonWithPieces,
  Dozen,
  PermissionModule,
  Permissions,
  Piece,
  ShipmentAccepted,
  ShipmentCancelled,
  ShipmentCompleted,
  ShipmentDelivered,
  ShipmentPartialDelivered,
  ShipmentPickedUp,
  ShipmentRejected,
  ShipmentSubmitted,
  SuperAdmin,
  UTradeTransport,
} from "../../../../utils/constants";
import Method from "../../../../utils/methods";
import { CustomSelectTable } from "../../../custom/Select/CustomSelectTable";
import GoodCancelRequest from "../../../modals/goods-request-cancel-request";
import { useAuth } from "../../auth";
import clsx from "clsx";
import { IMAGES } from "../../../../utils/assetsAndPermissions";
import EscalationModal from "../../../modals/EscalationModal";
import ConfirmChanges from "../../../modals/confirm-changes";
import APICallService from "../../../../api/apiCallService";
import {
  ESCALATIONS,
  manageProduct,
  PURCHASEORDER,
} from "../../../../api/apiEndPoints";
import GreenTickIcon from "../../../../_admin/assets/media/svg_uTrade/green-tick.svg";
import UpdateSellingPrice from "../../../modals/update-selling-price";
import { error, success } from "../../../../Global/toast";
import { useWarehouse } from "../../../context/WarehouseContext";
import ImageWithFallback from "../../_common/components/image-with-fallback";
import { PriceCatalog, PurchaseOrder } from "../../../../types";
import InwardDeliveryFiles from "../../inward/components/inward-delivery-files";
import InwardPoSummary from "../../inward/components/inward-po-summary";
import { calculateGPMargin } from "src/utils";
import PoSupplierGroupCard from "./po-supplier-group";

const PurchaseOrderDetailsV2 = (props: any) => {
  const navigate = useNavigate();
  const { selectedWarehouse } = useWarehouse();
  const { state }: any = useLocation();
  const { currentUser } = useAuth();
  const [confirmModal, setConfirmModal] = useState(false);
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [approveLoading, setApproveLoading] = useState(false);
  const [showRejectConfirm, setShowRejectConfirm] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingGrn, setLoadingGrn] = useState(false);
  const [initLoader, setInitLoader] = useState(true);
  const [cancelLoading, setCancelLoading] = useState(false);

  const [details, setDetails] = useState<PurchaseOrder | any>(null);

  const [show, setShow] = useState(false);
  const [showEscalationModal, setShowEscalationModal] = useState(false);
  const [escalations, setEscalations] = useState<any>([]);
  const [showSellingPriceModal, setShowSellingPriceModal] = useState(false);
  const [currentVariantData, setCurrentVariantData] = useState<any>();
  const [download, setDownload] = useState(false);
  const [downloadGRN, setDownloadGRN] = useState<string | null>(null);
  const [deliveryRefKey, setDeliverRefKey] = useState<any>("");
  const [updatedSellingPrices, setUpdatedSellingPrices] = useState<
    Record<string, Record<string, string>>
  >({});
  const [saveLoading, setSaveLoading] = useState<Record<string, boolean>>({});
  const [saveSingleLoading, setSaveSingleLoading] = useState<
    Record<string, boolean>
  >({});
  // Rows explicitly saved in this session (keyed `${refKey}_${vIndex}_${qtIndex}`), so the
  // "Saved" tick only shows for prices we actually just persisted, not stale backend defaults.
  const [savedThisSession, setSavedThisSession] = useState<Set<string>>(
    new Set(),
  );
  // Active selling price fetched from the price catalog, keyed by `${variantId}_${quantityType}`
  const [activeSellingPrices, setActiveSellingPrices] = useState<
    Record<string, number>
  >({});
  const [sellingPriceLoading, setSellingPriceLoading] = useState<
    Record<string, boolean>
  >({});
  // Pending confirmation shown when a re-fetched catalog price differs from what is about to be saved
  const [priceChangeConfirm, setPriceChangeConfirm] = useState<{
    mode: "single" | "all";
    delivery: any;
    vIndex?: number;
    qtIndex?: number;
    changes: { label: string; oldPrice: number; newPrice: number }[];
  } | null>(null);
  const [priceChangeLoading, setPriceChangeLoading] = useState(false);
  // Guards against retrying auto price-catalog creation more than once per variant/warehouse
  const autoCreateAttemptedRef = useRef<Set<string>>(new Set());

  const handleEscalationSubmit = async (formData: FormData) => {
    try {
      setLoading(true);

      const apiService = new APICallService(
        ESCALATIONS.addEscalation,
        formData,
      );

      const response = await apiService.callAPI();
      const parsed = response ? response : null;

      if (parsed) {
        setShowEscalationModal(false);
        //   await fetchEscaltionsList();
      }
    } catch (error) {
      console.error("Escalation failed", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPurchaseOrderInfo = async () => {
    setLoading(true);
    setInitLoader(true);
    setDetails(null);
    let apiService = new APICallService(
      PURCHASEORDER.purchaseOrderInfo,
      state.id,
    );
    let response = await apiService.callAPI();
    response = response ? response : null;
    if (response) {
      const targetDeliveryRefKey = state?.openDeliveryRefKey;
      const shouldOpenFirstDelivery =
        !targetDeliveryRefKey && state?.openGrnSection;
      const resp: any = {
        ...response,
        deliveries:
          response.deliveries && response.deliveries.length
            ? response.deliveries.map((d: any) => ({
              ...d,
              isOpen:
                (targetDeliveryRefKey !== undefined &&
                  targetDeliveryRefKey !== null &&
                  String(d.refKey) === String(targetDeliveryRefKey)) ||
                (shouldOpenFirstDelivery &&
                  String(d.refKey) ===
                  String(response.deliveries?.[0]?.refKey)),
            }))
            : response.deliveries || [],
      };
      setDetails(resp);
    } else {
      setDetails(null);
    }
    setInitLoader(false);

    setLoading(false);
  };

  const fetchEscaltionsList = async () => {
    try {
      setLoading(true);

      let params: any = {
        needCount: true,
        limit: 1,
      };

      const apiService = new APICallService(
        ESCALATIONS.listEscalation,
        params,
        "",
        "",
        "",
        "",
        0,
      );

      const response = await apiService.callAPI();

      if (response) {
        setEscalations(response?.records || []);
      }
    } catch (error) {
      console.error("Escalations list API failed", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      setInitLoader(true);
      await fetchPurchaseOrderInfo();
      await fetchEscaltionsList();
      setInitLoader(false);
    })();
  }, [state?.id]);

  // Builds a savePriceCatalogEntry payload straight from the PO's own `variants` data (product/tax/quantityTypes),
  // grouping the flat quantityTypes (each carrying its own warehouseId) into per-warehouse pricing entries.
  const buildAutoPriceCatalogPayload = (variantId: string) => {
    const poVariant = details?.variants?.find(
      (v: any) => (v.variant?._id || v.variant) === variantId,
    );
    if (!poVariant?.quantityTypes?.length) return null;

    const pricingByWarehouse = new Map<string, any>();
    poVariant.quantityTypes.forEach((qt: any) => {
      const warehouseId = qt.warehouseId || selectedWarehouse?._id;
      if (!warehouseId) return;
      if (!pricingByWarehouse.has(warehouseId)) {
        pricingByWarehouse.set(warehouseId, {
          active: true,
          warehouseId,
          defaultWarehouse: qt.defaultWarehouse ?? false,
          quantityTypes: [],
        });
      }
      pricingByWarehouse.get(warehouseId).quantityTypes.push({
        type: qt.type,
        costPrice: qt.purchaseAmount ?? 0,
        sellingPrice: qt.amount ?? 0,
      });
    });

    if (!pricingByWarehouse.size) return null;

    return {
      productId: poVariant.product,
      variantId,
      title: poVariant.variantName,
      taxed: !poVariant.taxFree,
      taxPercentage: poVariant.productTax ?? 18,
      enableForAllWarehouses: false,
      draftPOEnabled: true,
      daysOfInventoryHolding: 0,
      pricing: Array.from(pricingByWarehouse.values()),
    };
  };

  // Auto-creates a price catalog entry for a variant that has none yet, using this PO's own purchase/selling amounts
  const autoCreatePriceCatalogEntry = async (
    variantId: string,
  ): Promise<boolean> => {
    const payload = buildAutoPriceCatalogPayload(variantId);
    if (!payload) return false;

    try {
      const response = await new APICallService(
        manageProduct.savePriceCatalogEntrySystem,
        [payload],
        "",
        "",
        "",
        "",
      ).callAPI();
      return !response?.error;
    } catch (err) {
      console.error("Failed to auto-create price catalog entry", err);
      return false;
    }
  };

  // Fetches the active pricing entry for a variant to lock the "New selling price" input; returns the freshly fetched prices keyed by quantityType
  const fetchActiveSellingPrice = async (
    variantId: string,
  ): Promise<Record<number, number>> => {
    const freshPrices: Record<number, number> = {};
    if (!variantId || !selectedWarehouse?._id) return freshPrices;
    try {
      setSellingPriceLoading((prev) => ({ ...prev, [variantId]: true }));

      const apiService = new APICallService(
        manageProduct.getPriceCatalogEntry,
        `${variantId}?project=${encodeURIComponent(
          JSON.stringify({ pricing: 1 }),
        )}&warehouse=${selectedWarehouse._id}`,
        "",
        "",
        "",
        "",
      );
      const response: PriceCatalog = await apiService.callAPI();
      const payload = response?.pricing || [];

      // Active entry for the selected warehouse determines the locked selling price per quantityType
      const activeEntry = payload.find(
        (p) => p.active && p.warehouseId === selectedWarehouse._id,
      );
      const activeQuantityTypes = activeEntry?.quantityTypes || [];

      // Nothing in the price catalog yet for this variant/warehouse - create one automatically from the PO data, then retry
      const attemptKey = `${variantId}_${selectedWarehouse._id}`;
      if (!activeEntry && !autoCreateAttemptedRef.current.has(attemptKey)) {
        autoCreateAttemptedRef.current.add(attemptKey);
        const created = await autoCreatePriceCatalogEntry(variantId);
        if (created) {
          return fetchActiveSellingPrice(variantId);
        }
      }

      setActiveSellingPrices((prev) => {
        const updated = { ...prev };
        activeQuantityTypes.forEach((qt: any) => {
          // Only a real (>0) active price counts as a valid selling price
          if (qt.sellingPrice > 0) {
            updated[`${variantId}_${qt.type}`] = qt.sellingPrice;
            freshPrices[qt.type] = qt.sellingPrice;
          } else {
            delete updated[`${variantId}_${qt.type}`];
          }
        });
        return updated;
      });
    } catch (err) {
      console.error("Failed to fetch active selling price", err);
    } finally {
      setSellingPriceLoading((prev) => ({ ...prev, [variantId]: false }));
    }
    return freshPrices;
  };

  useEffect(() => {
    if (!details?.deliveries?.length || !selectedWarehouse?._id) return;

    const variantIds = new Set<string>();
    details.deliveries.forEach((delivery: any) => {
      delivery.variants?.forEach((v: any) => {
        const vId = v?.variant?._id || v?.variant;
        if (vId) variantIds.add(vId);
      });
    });

    variantIds.forEach((variantId) => fetchActiveSellingPrice(variantId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details?.deliveries?.length, selectedWarehouse?._id]);

  // Auto-fill the (non-editable) "New selling price" input from the fetched active catalog price
  useEffect(() => {
    if (!details?.deliveries?.length) return;

    setUpdatedSellingPrices((prev) => {
      const updated = { ...prev };
      let changed = false;

      details.deliveries.forEach((delivery: any) => {
        const deliveryPrices = { ...(updated[delivery.refKey] || {}) };
        delivery.variants?.forEach((v: any, vIndex: number) => {
          (v.quantityTypes || []).forEach((qt: any, qtIndex: number) => {
            if (qt.priceUpdated || Number(qt.sellingAmount) > 0) return;

            const variantId = v.variant?._id || v.variant;
            const catalogPrice = activeSellingPrices[`${variantId}_${qt.type}`];
            const key = `price_${vIndex}_${qtIndex}`;
            if (
              catalogPrice !== undefined &&
              deliveryPrices[key] === undefined
            ) {
              deliveryPrices[key] = catalogPrice.toString();
              changed = true;
            }
          });
        });
        updated[delivery.refKey] = deliveryPrices;
      });

      return changed ? updated : prev;
    });
  }, [details?.deliveries, activeSellingPrices]);

  useEffect(() => {
    if (!details || !state?.openDeliveryRefKey) {
      return;
    }

    const scrollToDelivery = () => {
      const element = document.getElementById(
        `delivery-${state.openDeliveryRefKey}`,
      );

      if (!element) {
        return;
      }

      const scrollTop =
        element.getBoundingClientRect().top + window.scrollY - 100;

      window.scrollTo({
        top: Math.max(scrollTop, 0),
        behavior: "smooth",
      });
    };

    scrollToDelivery();
    const timeoutId = window.setTimeout(scrollToDelivery, 250);

    return () => window.clearTimeout(timeoutId);
  }, [details, state?.openDeliveryRefKey]);

  useEffect(() => {
    if (!details || !state?.openGrnSection) {
      return;
    }

    const scrollToGrnSection = () => {
      const element = document.getElementById("grn-section");

      if (!element) {
        return;
      }

      const scrollTop =
        element.getBoundingClientRect().top + window.scrollY - 100;

      window.scrollTo({
        top: Math.max(scrollTop, 0),
        behavior: "smooth",
      });
    };

    scrollToGrnSection();
    const timeoutId = window.setTimeout(scrollToGrnSection, 250);

    return () => window.clearTimeout(timeoutId);
  }, [details, state?.openGrnSection]);

  const handleStatus = async (status: any) => {
    setLoading(true);
    let form = new FormData();
    form.append("status", status);
    const recordId = details?._id || state?.id;

    let apiService = new APICallService(
      PURCHASEORDER.updatePurchaseOrderStatus,
      form,
      { id: recordId },
      "",
      "",
      "",
      AllModules,
      selectedWarehouse?._id || null,
    );
    let response = await apiService.callAPI();

    setDetails(response);
    setLoading(false);
    setInitLoader(true);
    await fetchPurchaseOrderInfo();
    setInitLoader(false);
  };

  const openMenuOnClick = async (id: any) => {
    setShow(true);
  };
  const onMenuClose = async () => {
    setShow(false);
  };
  const onMenuOpen = async (id: any) => {
    setShow(true);
  };

  const handleOption = async (event: any, data: any) => {
    if (!details) {
      return;
    }

    if (event.value === 1) {
      navigate("/UProcure/purchase-order-management/edit-purchase-order", {
        state: details,
      });
    } else if (event.value === 2) {
      setConfirmModal(true);
    }
  };
  const handleCancel = async (adminNote?: string) => {
    const recordId = details?._id || state?.id;
    if (!recordId) {
      error("Purchase order not found");
      return;
    }

    setCancelLoading(true);
    try {
      setLoading(true);
      const form = new FormData();
      form.append("status", ShipmentCancelled.toString());
      form.append("reason", adminNote?.toString() || "");

      const apiService = new APICallService(
        PURCHASEORDER.updatePurchaseOrderStatus,
        form,
        { id: recordId },
        "",
        "",
        "",
        AllModules,
        selectedWarehouse?._id || null,
      );

      const response = await apiService.callAPI();
      const parsedResponse =
        typeof response === "string" ? JSON.parse(response) : response;

      if (!parsedResponse) {
        error("Failed to cancel PO. Please try again.");
        return;
      }

      setDetails(parsedResponse);
      success("Purchase order cancelled successfully.");
      setConfirmModal(false);
      navigate("/UProcure/purchase-order-management", { state: 4 });
    } catch (err) {
      console.error("Failed to cancel PO", err);
      error("Failed to cancel PO. Please try again.");
    } finally {
      setLoading(false);
      setCancelLoading(false);
    }
  };

  const handleGrnToggle = (index: number) => {
    setDetails((prev: any) => {
      if (!prev) return prev;
      const deliveries =
        prev.deliveries && prev.deliveries.length
          ? prev.deliveries.map((item: any, i: number) => ({
            ...item,
            isOpen: i === index ? !item.isOpen : item.isOpen,
          }))
          : [];
      return { ...prev, deliveries };
    });
  };

  const handlePartialDeliveryToggle = (index: number) => {
    setDetails((prev: any) => {
      if (!prev) return prev;
      const deliveries =
        prev.deliveries && prev.deliveries.length
          ? prev.deliveries.map((item: any, i: number) => ({
            ...item,
            isOpen: i === index ? !item.isOpen : item.isOpen,
          }))
          : [];
      return { ...prev, deliveries };
    });
  };

  const getShipmentTypeLabel = (type?: number): string | undefined => {
    switch (type) {
      case 1:
        return "Pending Approval";
      case 2:
        return "Delivery Delay";
      case 3:
        return "Payment Delay";
      default:
        return undefined;
    }
  };

  const handleHideSellingPriceModal = () => {
    setShowSellingPriceModal(false);
    setCurrentVariantData(undefined);
    setDeliverRefKey("");
  };

  const handlePriceChange = (
    deliveryRefKey: string,
    variantItem: any,
    targetQtyType: any,
    newValue: string,
    vIndex: number,
    qtIndex: number,
  ) => {
    if (newValue === "") {
      setUpdatedSellingPrices((prev) => {
        const deliveryPrices = { ...(prev[deliveryRefKey] || {}) };
        const targetKey = `price_${vIndex}_${qtIndex}`;
        deliveryPrices[targetKey] = "";

        return {
          ...prev,
          [deliveryRefKey]: deliveryPrices,
        };
      });
      return;
    }

    const val = newValue.replace(/[^0-9.]/g, "");

    // Prevent 0 values
    if (val === "0") return;

    const numericValue = parseFloat(val);

    setUpdatedSellingPrices((prev) => {
      const deliveryPrices = { ...(prev[deliveryRefKey] || {}) };

      // Calculate percentage change based on base amount
      const baseAmount = targetQtyType.amount;
      const percentageChange =
        !isNaN(numericValue) && baseAmount > 0
          ? (numericValue - baseAmount) / baseAmount
          : 0;

      // Update ONLY the target row uniquely by its indices
      const targetKey = `price_${vIndex}_${qtIndex}`;
      deliveryPrices[targetKey] = val;

      return {
        ...prev,
        [deliveryRefKey]: deliveryPrices,
      };
    });
  };

  const handleSaveAllPrices = async (delivery: any) => {
    const deliveryRefKey = delivery.refKey;
    const deliveryPrices = updatedSellingPrices[deliveryRefKey];
    if (!deliveryPrices) return;

    setSaveLoading((prev) => ({ ...prev, [deliveryRefKey]: true }));
    try {
      // Structure the payload for the updateSellingPrice API
      const variantsPayload = delivery.variants.map(
        (v: any, vIndex: number) => ({
          variant: v.variant?._id || v.variant,
          quantityTypes: v.quantityTypes.map((qt: any, qtIndex: number) => {
            const key = `price_${vIndex}_${qtIndex}`;
            if (!deliveryPrices[key]) return qt;

            const updatedAmount = parseFloat(deliveryPrices[key]);

            // Calculate the percentage change for this specific row
            const baseAmount = qt.amount;
            const percentageChange =
              baseAmount > 0 ? (updatedAmount - baseAmount) / baseAmount : 0;

            // Scale the discount amount
            const baseDiscountAmt = qt.discountAmt || 0;
            const updatedDiscountAmt = Math.round(
              baseDiscountAmt * (1 + percentageChange),
            );

            // Handle discountsByQuantities
            let updatedDiscountsByQuantities = [];
            if (qt.discountByQuantitiesEnabled) {
              // Scale existing ranges and update both discountAmt and amount keys
              updatedDiscountsByQuantities = (
                qt.discountsByQuantities || []
              ).map((d: any) => {
                const scaledValue = Math.round(
                  (d.discountAmt || d.amount || 0) * (1 + percentageChange),
                );
                return {
                  ...d,
                  discountAmt: scaledValue,
                  amount: scaledValue,
                };
              });
            } else {
              updatedDiscountsByQuantities = [];
            }

            return {
              ...qt,
              sellingAmount: updatedAmount,
              sellingDiscountAmt: updatedDiscountAmt,
              sellingDiscountAmtEnabled: qt.discountByQuantitiesEnabled
                ? false
                : updatedDiscountAmt > 0
                  ? true
                  : false,
              sellingDiscountByQuantitiesEnabled:
                qt.discountByQuantitiesEnabled,
              sellingDiscountsByQuantities: updatedDiscountsByQuantities,
              priceUpdated: qt.priceUpdated || false,
            };
          }),
        }),
      );

      const data = {
        variants: variantsPayload,
        recordId: details?._id,
        refKey: deliveryRefKey,
      };

      const apiCallService = new APICallService(
        PURCHASEORDER.updateSellingPrice,
        data,
        { id: details?._id },
        "",
        "",
        false,
        AllModules,
        selectedWarehouse?.value || null,
      );

      const response = await apiCallService.callAPI();
      if (response) {
        success(`Selling prices for ${deliveryRefKey} updated successfully!`);

        // Update local details state to reflect changes without refresh
        setDetails((prev: any) => {
          if (!prev) return prev;
          const newDetails = { ...prev };
          newDetails.deliveries = newDetails.deliveries?.map((d: any) => {
            if (d.refKey === deliveryRefKey) {
              return {
                ...d,
                variants: d.variants.map((v: any, vIndex: number) => ({
                  ...v,
                  quantityTypes: v.quantityTypes.map(
                    (qt: any, qtIndex: number) => {
                      const key = `price_${vIndex}_${qtIndex}`;
                      if (deliveryPrices[key]) {
                        const updatedAmount = parseFloat(deliveryPrices[key]);

                        // Calculate same percentage for local state sync
                        const baseAmount = qt.amount;
                        const percentageChange =
                          baseAmount > 0
                            ? (updatedAmount - baseAmount) / baseAmount
                            : 0;
                        const baseDiscountAmt = qt.discountAmt || 0;
                        const updatedDiscountAmt = Math.round(
                          baseDiscountAmt * (1 + percentageChange),
                        );

                        let updatedDiscountsByQuantities = [];
                        if (qt.discountByQuantitiesEnabled) {
                          updatedDiscountsByQuantities = (
                            qt.discountsByQuantities || []
                          ).map((d: any) => {
                            const scaledValue = Math.round(
                              (d.discountAmt || d.amount || 0) *
                              (1 + percentageChange),
                            );
                            return {
                              ...d,
                              discountAmt: scaledValue,
                              amount: scaledValue,
                            };
                          });
                        } else {
                          updatedDiscountsByQuantities = [];
                        }

                        return {
                          ...qt,
                          sellingAmount: updatedAmount,
                          priceUpdated: true,
                          sellingDiscountAmt: updatedDiscountAmt,
                          sellingDiscountAmtEnabled:
                            qt.discountByQuantitiesEnabled
                              ? false
                              : updatedDiscountAmt > 0
                                ? true
                                : false,
                          sellingDiscountByQuantitiesEnabled:
                            qt.discountByQuantitiesEnabled,
                          sellingDiscountsByQuantities:
                            updatedDiscountsByQuantities,
                        };
                      }
                      return qt;
                    },
                  ),
                })),
              };
            }
            return d;
          });
          return newDetails;
        });

        // Mark every row included in this save so the "Saved" tick reflects a real save this session
        setSavedThisSession((prev) => {
          const next = new Set(prev);
          delivery.variants.forEach((v: any, vIndex: number) => {
            v.quantityTypes.forEach((qt: any, qtIndex: number) => {
              if (deliveryPrices[`price_${vIndex}_${qtIndex}`]) {
                next.add(`${deliveryRefKey}_${vIndex}_${qtIndex}`);
              }
            });
          });
          return next;
        });

        // Clear local inputs for this delivery
        setUpdatedSellingPrices((prev) => {
          const newState = { ...prev };
          delete newState[deliveryRefKey];
          return newState;
        });
      }
    } catch (err) {
      console.error("Failed to save selling prices", err);
      error("Failed to save selling prices. Please try again.");
    } finally {
      setSaveLoading((prev) => ({ ...prev, [deliveryRefKey]: false }));
    }
  };

  const handleSaveSinglePrice = async (
    delivery: any,
    vIndex: number,
    qtIndex: number,
  ) => {
    const deliveryRefKey = delivery.refKey;
    const deliveryPrices = updatedSellingPrices[deliveryRefKey];
    const key = `price_${vIndex}_${qtIndex}`;

    if (!deliveryPrices || !deliveryPrices[key]) return;

    const loadingKey = `${deliveryRefKey}_${vIndex}_${qtIndex}`;
    setSaveSingleLoading((prev) => ({ ...prev, [loadingKey]: true }));
    try {
      const variantsPayload = delivery.variants.map(
        (v: any, curVIndex: number) => ({
          variant: v.variant?._id || v.variant,
          quantityTypes: v.quantityTypes.map((qt: any, curQtIndex: number) => {
            const isTargetRow = curVIndex === vIndex && curQtIndex === qtIndex;
            if (!isTargetRow) return qt;

            const updatedAmount = parseFloat(deliveryPrices[key]);
            const baseAmount = qt.amount;
            const percentageChange =
              baseAmount > 0 ? (updatedAmount - baseAmount) / baseAmount : 0;
            const baseDiscountAmt = qt.discountAmt || 0;
            const updatedDiscountAmt = Math.round(
              baseDiscountAmt * (1 + percentageChange),
            );

            let updatedDiscountsByQuantities = [];
            if (qt.discountByQuantitiesEnabled) {
              updatedDiscountsByQuantities = (
                qt.discountsByQuantities || []
              ).map((d: any) => {
                const scaledValue = Math.round(
                  (d.discountAmt || d.amount || 0) * (1 + percentageChange),
                );
                return {
                  ...d,
                  discountAmt: scaledValue,
                  amount: scaledValue,
                };
              });
            } else {
              updatedDiscountsByQuantities = [];
            }

            return {
              ...qt,
              sellingAmount: updatedAmount,
              sellingDiscountAmt: updatedDiscountAmt,
              sellingDiscountAmtEnabled: qt.discountByQuantitiesEnabled
                ? false
                : updatedDiscountAmt > 0
                  ? true
                  : false,
              sellingDiscountByQuantitiesEnabled:
                qt.discountByQuantitiesEnabled,
              sellingDiscountsByQuantities: updatedDiscountsByQuantities,
              priceUpdated: qt.priceUpdated || false,
            };
          }),
        }),
      );

      const data = {
        variants: variantsPayload,
        recordId: details?._id,
        refKey: deliveryRefKey,
      };

      const apiCallService = new APICallService(
        PURCHASEORDER.updateSellingPrice,
        data,
        { id: details?._id },
        "",
        "",
        false,
        AllModules,
        selectedWarehouse?.value || null,
      );

      const response = await apiCallService.callAPI();
      if (response) {
        success(`Selling price updated successfully!`);

        setDetails((prev: any) => {
          if (!prev) return prev;
          const newDetails = { ...prev };
          newDetails.deliveries = newDetails.deliveries?.map((d: any) => {
            if (d.refKey === deliveryRefKey) {
              return {
                ...d,
                variants: d.variants.map((v: any, curVIndex: number) => ({
                  ...v,
                  quantityTypes: v.quantityTypes.map(
                    (qt: any, curQtIndex: number) => {
                      if (curVIndex === vIndex && curQtIndex === qtIndex) {
                        const updatedAmount = parseFloat(deliveryPrices[key]);
                        const baseAmount = qt.amount;
                        const percentageChange =
                          baseAmount > 0
                            ? (updatedAmount - baseAmount) / baseAmount
                            : 0;
                        const baseDiscountAmt = qt.discountAmt || 0;
                        const updatedDiscountAmt = Math.round(
                          baseDiscountAmt * (1 + percentageChange),
                        );

                        let updatedDiscountsByQuantities = [];
                        if (qt.discountByQuantitiesEnabled) {
                          updatedDiscountsByQuantities = (
                            qt.discountsByQuantities || []
                          ).map((d: any) => {
                            const scaledValue = Math.round(
                              (d.discountAmt || d.amount || 0) *
                              (1 + percentageChange),
                            );
                            return {
                              ...d,
                              discountAmt: scaledValue,
                              amount: scaledValue,
                            };
                          });
                        } else {
                          updatedDiscountsByQuantities = [];
                        }

                        return {
                          ...qt,
                          sellingAmount: updatedAmount,
                          priceUpdated: true,
                          sellingDiscountAmt: updatedDiscountAmt,
                          sellingDiscountAmtEnabled:
                            qt.discountByQuantitiesEnabled
                              ? false
                              : updatedDiscountAmt > 0
                                ? true
                                : false,
                          sellingDiscountByQuantitiesEnabled:
                            qt.discountByQuantitiesEnabled,
                          sellingDiscountsByQuantities:
                            updatedDiscountsByQuantities,
                        };
                      }
                      return qt;
                    },
                  ),
                })),
              };
            }
            return d;
          });
          return newDetails;
        });

        // Mark this row so the "Saved" tick reflects a real save this session
        setSavedThisSession((prev) =>
          new Set(prev).add(`${deliveryRefKey}_${vIndex}_${qtIndex}`),
        );

        setUpdatedSellingPrices((prev) => {
          const newState = { ...prev };
          if (newState[deliveryRefKey]) {
            const newDeliveryPrices = { ...newState[deliveryRefKey] };
            delete newDeliveryPrices[key];
            if (Object.keys(newDeliveryPrices).length === 0) {
              delete newState[deliveryRefKey];
            } else {
              newState[deliveryRefKey] = newDeliveryPrices;
            }
          }
          return newState;
        });
      }
    } catch (err) {
      console.error("Failed to save selling price", err);
      error("Failed to save selling price. Please try again.");
    } finally {
      setSaveSingleLoading((prev) => ({ ...prev, [loadingKey]: false }));
    }
  };

  // Re-checks the catalog price for a single row before saving; prompts for confirmation if it changed
  const confirmAndSaveSingle = async (
    delivery: any,
    vIndex: number,
    qtIndex: number,
    variantId: string,
    quantityType: number,
    pendingValue: string,
  ) => {
    const loadingKey = `${delivery.refKey}_${vIndex}_${qtIndex}`;
    if (saveSingleLoading[loadingKey] || priceChangeLoading) return;

    if (!variantId) {
      handleSaveSinglePrice(delivery, vIndex, qtIndex);
      return;
    }

    setPriceChangeLoading(true);
    const freshPrices = await fetchActiveSellingPrice(variantId);
    setPriceChangeLoading(false);

    const freshPrice = freshPrices[quantityType];
    const oldPrice = Number(pendingValue) || 0;

    if (freshPrice !== undefined && freshPrice !== oldPrice) {
      setUpdatedSellingPrices((prev) => ({
        ...prev,
        [delivery.refKey]: {
          ...(prev[delivery.refKey] || {}),
          [`price_${vIndex}_${qtIndex}`]: freshPrice.toString(),
        },
      }));
      setPriceChangeConfirm({
        mode: "single",
        delivery,
        vIndex,
        qtIndex,
        changes: [{ label: "", oldPrice, newPrice: freshPrice }],
      });
      return;
    }

    handleSaveSinglePrice(delivery, vIndex, qtIndex);
  };

  // Re-checks the catalog price for every pending row in a delivery before saving in bulk
  const confirmAndSaveAll = async (delivery: any) => {
    const deliveryRefKey = delivery.refKey;
    if (saveLoading[deliveryRefKey] || priceChangeLoading) return;

    const deliveryPrices = { ...(updatedSellingPrices[deliveryRefKey] || {}) };
    const pendingEntries = Object.entries(deliveryPrices).filter(([, v]) => v);
    if (!pendingEntries.length) return;

    setPriceChangeLoading(true);
    const freshPricesByVariant: Record<string, Record<number, number>> = {};
    const changes: { label: string; oldPrice: number; newPrice: number }[] = [];

    for (const [key, value] of pendingEntries) {
      const match = key.match(/^price_(\d+)_(\d+)$/);
      if (!match) continue;
      const vIndex = Number(match[1]);
      const qtIndex = Number(match[2]);
      const variantItem = delivery.variants?.[vIndex];
      const qt = variantItem?.quantityTypes?.[qtIndex];
      if (!variantItem || !qt) continue;

      const variantId = variantItem.variant?._id || variantItem.variant;
      if (!variantId) continue;
      if (!freshPricesByVariant[variantId]) {
        freshPricesByVariant[variantId] = await fetchActiveSellingPrice(
          variantId,
        );
      }

      const freshPrice = freshPricesByVariant[variantId][qt.type];
      const oldPrice = Number(value) || 0;

      if (freshPrice !== undefined && freshPrice !== oldPrice) {
        changes.push({
          label: variantItem.variantName || "Item",
          oldPrice,
          newPrice: freshPrice,
        });
        deliveryPrices[key] = freshPrice.toString();
      }
    }
    setPriceChangeLoading(false);

    if (changes.length) {
      setUpdatedSellingPrices((prev) => ({
        ...prev,
        [deliveryRefKey]: deliveryPrices,
      }));
      setPriceChangeConfirm({ mode: "all", delivery, changes });
      return;
    }

    handleSaveAllPrices(delivery);
  };

  const handleConfirmPriceChange = () => {
    if (!priceChangeConfirm) return;
    const { mode, delivery, vIndex, qtIndex } = priceChangeConfirm;
    setPriceChangeConfirm(null);
    if (mode === "single" && vIndex !== undefined && qtIndex !== undefined) {
      handleSaveSinglePrice(delivery, vIndex, qtIndex);
    } else if (mode === "all") {
      handleSaveAllPrices(delivery);
    }
  };

  const getTitle = (type: any) => {
    if (type == Dozen) return "Dozen";
    if (type == CartonWithDozens) return "Cartoon";
    if (type == CartonWithPieces) return "Pieces";
  };

  const getStockWithLabel = (qtyTypes: any[]) => {
    return qtyTypes
      ?.flat()
      ?.map((item: any) => {
        const label =
          item.type === CartonWithDozens
            ? "Carton With Dozens"
            : item.type === CartonWithPieces
              ? "Carton With Pieces"
              : item.type === Dozen
                ? "Dozen"
                : item.type === Piece
                  ? "Pieces"
                  : "";

        return label
          ? `${item.stockCount - item?.rejectedStockCount} ${label}`
          : null;
      })
      ?.filter(Boolean)
      ?.join(", ");
  };

  const handleDownloadPO = async () => {
    setDownload(true);
    let apiService = new APICallService(
      PURCHASEORDER.poDownload,
      state.id,
      undefined,
      "blob",
      "",
      "",
      AllModules,
      selectedWarehouse?.value || null,
    );
    let response: any = await apiService.callAPI();
    if (response) {
      const pdfBlob = new Blob([response], { type: "application/pdf" });
      const downloadLink = document.createElement("a");
      downloadLink.href = URL.createObjectURL(pdfBlob);
      downloadLink.download = "PO_" + details.refKey + ".pdf";
      downloadLink.click();
    } else {
      error("Failed to download report. Please try again.");
    }
    setDownload(false);
  };

  const handleDownloadAllGRNs = async () => {
    setLoadingGrn(true);
    let apiService = new APICallService(
      PURCHASEORDER.allGrnDownload,
      {},
      { id: details._id },
      "blob",
      "",
      "",
      AllModules,
    );
    let response: any = await apiService.callAPI();
    if (response) {
      const pdfBlob = new Blob([response], { type: "application/pdf" });
      const downloadLink = document.createElement("a");
      downloadLink.href = URL.createObjectURL(pdfBlob);
      downloadLink.download = "GRN_All_" + details.refKey + ".pdf";
      downloadLink.click();
    } else {
      error("Failed to download. Please try again.");
    }
    setLoadingGrn(false);
  };

  const ShipmentStatusLabelMap: Record<number, string> = {
    [ShipmentSubmitted]: "Submitted",
    [ShipmentAccepted]: "Accepted",
    [ShipmentCancelled]: "Cancelled",
    [ShipmentRejected]: "Rejected",
    [ShipmentPickedUp]: "Picked Up",
    [ShipmentDelivered]: "Delivered",
    [ShipmentCompleted]: "Completed",
    [ShipmentPartialDelivered]: "Partially Delivered",
  };

  const getShipmentStatusLabel = (status?: number | null): string => {
    if (!status) return "-";
    return ShipmentStatusLabelMap[status] || "Unknown";
  };

  const hasAvailableQty = (
    totalQuantities: any[] = [],
    deliveries: any[] = [],
  ) => {
    return totalQuantities.some((tq) => {
      const totalStock = tq.stockCount || 0;

      let received = 0;
      let rejected = 0;

      deliveries.forEach((delivery) => {
        delivery.variants.forEach((variant: any) => {
          variant.quantityTypes.forEach((qItem: any) => {
            if (qItem.type === tq.quantityType) {
              received += qItem.receivedStockCount || 0;
              rejected += qItem.rejectedStockCount || 0;
            }
          });
        });
      });

      const availableQty = totalStock - received - rejected;
      return availableQty > 0;
    });
  };

  const handleDownloadGRN = async (refKey: string) => {
    setDownloadGRN(refKey);
    const params = {
      refKey: refKey,
    };
    let apiService = new APICallService(
      PURCHASEORDER.grnDownload,
      params,
      { id: state.id },
      "blob",
      "",
      "",
      AllModules,
    );
    let response: any = await apiService.callAPI();
    if (response) {
      const pdfBlob = new Blob([response], { type: "application/pdf" });
      const downloadLink = document.createElement("a");
      downloadLink.href = URL.createObjectURL(pdfBlob);
      downloadLink.download = "GRN_" + refKey + ".pdf";
      downloadLink.click();
    } else {
      error("Failed to download. Please try again.");
    }
    setDownloadGRN(null);
  };

  return (
    <>
      {showSellingPriceModal && currentVariantData ? (
        <UpdateSellingPrice
          show={showSellingPriceModal}
          onHide={handleHideSellingPriceModal}
          deliveryRefKey={deliveryRefKey}
          variantData={currentVariantData}
          details={details}
          onSave={async (update: any) => {
            // Close modal and clear ref key
            setShowSellingPriceModal(false);
            setDeliverRefKey("");

            if (!update) return;

            // Update the details state in-place so UI reflects new sellingAmount without refetch
            setDetails((prev: any) => {
              if (!prev) return prev;
              const newDetails: any = { ...prev };

              // If deliveryRefKey provided, update within that delivery only
              if (update.deliveryRefKey) {
                newDetails.deliveries = newDetails.deliveries?.map(
                  (delivery: any) => {
                    if (delivery.refKey === update.deliveryRefKey) {
                      return {
                        ...delivery,
                        variants: delivery.variants?.map((v: any) => {
                          const vId = v?.variant?._id || v?.variant || null;
                          if (vId === update.variantId) {
                            return {
                              ...v,
                              quantityTypes: v.quantityTypes?.map((qt: any) =>
                                qt.type === update.quantityType
                                  ? {
                                    ...qt,
                                    sellingAmount: update.sellingAmount,
                                    sellingDiscountAmt:
                                      update.sellingDiscountAmt,
                                    sellingDiscountAmtEnabled:
                                      update.sellingDiscountAmtEnabled,
                                    sellingDiscountByQuantitiesEnabled:
                                      update.sellingDiscountByQuantitiesEnabled,
                                    sellingDiscountsByQuantities:
                                      update.sellingDiscountsByQuantities,
                                  }
                                  : qt,
                              ),
                            };
                          }
                          return v;
                        }),
                      };
                    }
                    return delivery;
                  },
                );
              } else {
                // Fallback: update across all deliveries
                newDetails.deliveries = newDetails.deliveries?.map(
                  (delivery: any) => ({
                    ...delivery,
                    variants: delivery.variants?.map((v: any) => {
                      const vId = v?.variant?._id || v?.variant || null;
                      if (vId === update.variantId) {
                        return {
                          ...v,
                          quantityTypes: v.quantityTypes?.map((qt: any) =>
                            qt.type === update.quantityType
                              ? {
                                ...qt,
                                sellingAmount: update.sellingAmount,
                                sellingDiscountAmt: update.sellingDiscountAmt,
                                sellingDiscountAmtEnabled:
                                  update.sellingDiscountAmtEnabled,
                                sellingDiscountByQuantitiesEnabled:
                                  update.sellingDiscountByQuantitiesEnabled,
                                sellingDiscountsByQuantities:
                                  update.sellingDiscountsByQuantities,
                              }
                              : qt,
                          ),
                        };
                      }
                      return v;
                    }),
                  }),
                );
              }

              return newDetails;
            });
          }}
        />
      ) : (
        <></>
      )}
      {showEscalationModal && (
        <EscalationModal
          show={showEscalationModal}
          onHide={() => setShowEscalationModal(false)}
          onSubmit={(data) => {
            const formData = new FormData();

            formData.append("shipment", details?._id);

            formData.append("escalationType", data.escalationType);
            formData.append("reason", data.reason);

            if (data.files?.length) {
              data.files.forEach((file: File) => {
                formData.append("image", file);
              });
            }

            handleEscalationSubmit(formData);
          }}
        />
      )}
      {showApproveConfirm && (
        <ConfirmChanges
          show={showApproveConfirm}
          onHide={() => setShowApproveConfirm(false)}
          title={"Approve Purchase Order"}
          save={"Yes, Approve"}
          onSave={async () => {
            try {
              setApproveLoading(true);
              await handleStatus(ShipmentAccepted);
            } finally {
              setApproveLoading(false);
              setShowApproveConfirm(false);
            }
          }}
          loading={approveLoading}
        />
      )}
      {showRejectConfirm && (
        <ConfirmChanges
          show={showRejectConfirm}
          onHide={() => setShowRejectConfirm(false)}
          title={"Reject Purchase Order"}
          save={"Yes, Reject"}
          onSave={async () => {
            try {
              setRejectLoading(true);
              await handleStatus(ShipmentRejected);
            } finally {
              setRejectLoading(false);
              setShowRejectConfirm(false);
            }
          }}
          loading={rejectLoading}
        />
      )}
      {priceChangeConfirm && (
        <Modal
          show={Boolean(priceChangeConfirm)}
          onHide={() => setPriceChangeConfirm(null)}
          dialogClassName="modal-dialog-centered min-w-lg-590px"
          className="border-r10px"
        >
          <Modal.Header>
            <Modal.Title className="fs-22 fw-bolder">
              Selling Price Changed
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="pt-5">
            <div className="row justify-content-center">
              <div className="col-lg-11">
                {priceChangeConfirm.changes.map((change, index) => (
                  <div
                    key={index}
                    className="bg-light-danger border-r10px p-3 mb-3"
                  >
                    <span className="fs-16 fw-500">
                      {change.label && `${change.label}: `}Selling price changed
                      from TSh {Method.getGeneralizedAmount(change.oldPrice)} to
                      TSh {Method.getGeneralizedAmount(change.newPrice)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer className="pt-0 pb-8 mb-4 border-top-0">
            <Row className="align-items-center justify-content-center g-3 w-100">
              <Col xs="auto">
                <Button className="uw-btn uw-btn-primary uw-btn-lg"
                  variant="primary"
                  size="lg"
                  onClick={handleConfirmPriceChange}
                >
                  <span className="fs-16 fw-bold">Yes, Continue</span>
                </Button>
              </Col>
              <Col xs="auto">
                <Button className="uw-btn uw-btn-danger uw-btn-lg"
                  variant="danger"
                  size="lg"
                  onClick={() => setPriceChangeConfirm(null)}
                >
                  <span className="fs-16 fw-bold">Cancel</span>
                </Button>
              </Col>
            </Row>
          </Modal.Footer>
        </Modal>
      )}
      <div className="p-5">
        <Row className="align-items-center g-3 mb-7">
          <Col xs className="d-flex gap-3 align-items-baseline">
            <h1 className="fs-22 fw-bolder mb-0">Purchase order details</h1>
            {details ? (
              details.status ? (
                <>
                  {" "}
                  <span className="bg-light-danger fs-12 fw-600 rounded-pill px-5 py-3">
                    {getShipmentStatusLabel(details.status)}
                  </span>
                </>
              ) : (
                <></>
              )
            ) : (
              <></>
            )}
          </Col>
          {!initLoader ? (
            <>
              {details ? (
                <>
                  {confirmModal && (
                    <GoodCancelRequest
                      show={confirmModal}
                      title={"Nile Perch Fisheries Ltd"}
                      save={"Yes, Cancel Request"}
                      message={""}
                      onHide={() => {
                        setConfirmModal(false);
                      }}
                      onSave={handleCancel}
                      loading={cancelLoading}
                    />
                  )}
                  <>
                    {/* {details.status === ShipmentSubmitted && details.requestor.userType === Seller && ( */}
                    <>
                      <Col xs="auto">
                        {(Method.hasPermission(
                          PermissionModule.POManagement,
                          Permissions.PO_CREATE_GRN,
                          currentUser,
                        ) ||
                          currentUser?.roleAndPermission
                            ?.isWarehouseManagerRole ||
                          currentUser?.roleAndPermission?.roleType ===
                          "R0012" ||
                          currentUser?.roleAndPermission?.roleType ===
                          "R0003") &&
                          (details?.status == ShipmentAccepted ||
                            details?.status == ShipmentPartialDelivered) &&
                          hasAvailableQty(
                            details?.totalQuantities,
                            details?.deliveries,
                          ) && (
                            <Button
                              className="uw-btn uw-btn-primary"
                              onClick={() => {
                                navigate(
                                  "/UProcure/goods-received-note/grn-details",
                                  {
                                    state: {
                                      details: details,
                                    },
                                  },
                                );
                              }}
                            >
                              Create GRN
                            </Button>
                          )}
                        {Method.hasPermission(
                          PermissionModule.POManagement,
                          Permissions.Download,
                          currentUser,
                        ) && (
                            <Button
                              className="uw-btn uw-btn-primary"
                              onClick={handleDownloadPO}
                              disabled={loading || download}
                            >
                              {!download && (
                                <span className="indicator-label fs-16 fw-bold">
                                  Download PO
                                </span>
                              )}
                              {download && (
                                <span
                                  className="indicator-progress fs-16 fw-bold"
                                  style={{ display: "block" }}
                                >
                                  Please wait...
                                  <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                                </span>
                              )}
                            </Button>
                          )}
                        {Method.hasPermission(
                          PermissionModule.POManagement,
                          Permissions.Download,
                          currentUser,
                        ) &&
                          (details.status === ShipmentPartialDelivered ||
                            details.status === ShipmentDelivered) &&
                          details?.deliveries?.length > 0 && (
                            <Button
                              className="uw-btn uw-btn-primary"
                              onClick={handleDownloadAllGRNs}
                              disabled={loadingGrn || download}
                            >
                              {!loadingGrn && (
                                <span className="indicator-label fs-16 fw-bold">
                                  Download All GRNs
                                </span>
                              )}
                              {loadingGrn && (
                                <span
                                  className="indicator-progress fs-16 fw-bold"
                                  style={{ display: "block" }}
                                >
                                  Please wait...
                                  <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                                </span>
                              )}
                            </Button>
                          )}
                      </Col>

                      {details.status == BulkShipmentSubmitted ||
                        details.status == ShipmentRejected ||
                        details.status == ShipmentCancelled ? (
                        <></>
                      ) : (
                        <>
                          <Col xs="auto">
                            <Button
                              className="uw-btn uw-btn-warning"
                              onClick={() => setShowEscalationModal(true)}
                            >
                              Raise Escalation
                            </Button>
                          </Col>
                        </>
                      )}

                      {(Method.hasPermission(
                        PermissionModule.POManagement,
                        Permissions.PO_APPROVE_AND_REJECT,
                        currentUser,
                      ) ||
                        currentUser?.roleAndPermission?.roleType === "R0001" ||
                        currentUser?.roleAndPermission?.roleType === "R0003" ||
                        currentUser?.roleAndPermission?.roleType === "R0012") &&
                        details.status === ShipmentSubmitted ? (
                        <Col xs="auto">
                          <Button
                            variant="btn-sm btn btn-primary"
                            className="uw-btn uw-btn-sm btn-sm"
                            onClick={() => {
                              setShowApproveConfirm(true);
                            }}
                          >
                            Approve PO
                          </Button>
                          <Button
                            variant="danger"
                            className="uw-btn uw-btn-danger uw-btn-sm ms-4 btn-sm"
                            onClick={() => {
                              setShowRejectConfirm(true);
                            }}
                          >
                            Reject PO
                          </Button>
                        </Col>
                      ) : (
                        <></>
                      )}
                    </>
                    {(details.status === BulkShipmentSubmitted ||
                      details.status === ShipmentSubmitted ||
                      details.status === ShipmentAccepted) && (
                        <Col xs="auto">
                          <CustomSelectTable
                            marginLeft={"0px"}
                            menuMargin={"-60px"}
                            width={"140px"}
                            placeholder={
                              <img
                                className="img-fluid"
                                width={47}
                                height={47}
                                src={threeDot}
                                alt=""
                              />
                            }
                            options={[
                              ...(details.status === BulkShipmentSubmitted ||
                                details.status === ShipmentSubmitted
                                ? [
                                  {
                                    label: (
                                      <button className="btn btn-link fs-12 fw-400 ms-3 p-4">
                                        Edit details
                                      </button>
                                    ),
                                    value: 1,
                                  },
                                ]
                                : []),
                              ...(details.status === ShipmentSubmitted ||
                                details.status === ShipmentAccepted
                                ? [
                                  {
                                    label: (
                                      <button className="btn btn-link fs-12 fw-400 text-danger ms-3 p-4">
                                        Cancel Request
                                      </button>
                                    ),
                                    value: 2,
                                  },
                                ]
                                : []),
                            ]}
                            backgroundColor="white"
                            show={show}
                            onMenuClose={onMenuClose}
                            openMenuOnClick={openMenuOnClick}
                            onMenuOpen={onMenuOpen}
                            onChange={(event: any) => {
                              handleOption(event, details);
                            }}
                          />
                        </Col>
                      )}
                  </>
                </>
              ) : null}
            </>
          ) : (
            <div className="d-flex justify-content-center">
              <Loader loading={initLoader} />
            </div>
          )}
        </Row>

        {!initLoader && details && (
          <>
            {details.supplierGroup && <PoSupplierGroupCard group={details.supplierGroup} />}
            <Row className="gy-5 mb-2">
              <Col md={6}>
                <Card className="card-stretch card-bordered">
                  <Card.Header className="bg-light align-items-center">
                    <div className="d-flex align-items-center justify-content-between w-100">
                      <Card.Title className="fs-22 fw-bolder">
                        Basic details
                      </Card.Title>
                      <span className="badge bg-e7f1fd fs-14 fw-600 text-primary p-3 border-r4px">
                        Request initiated for{" "}
                        {details.requestor.userType === SuperAdmin
                          ? "U-Trade"
                          : "Supplier"}
                      </span>
                    </div>
                  </Card.Header>
                  <Card.Body>
                    <Row className="mb-3">
                      <Col md={5} xs={4}>
                        <label className="fs-16 fw-500 text-dark">
                          Supplier name:
                        </label>
                      </Col>
                      <Col md={7} xs={8}>
                        <span className="fw-bold fs-16 fw-600 text-dark">
                          {details.business.ownerName}
                        </span>
                      </Col>
                    </Row>
                    {details.supplierGroup && (
                      <Row className="mb-3">
                        <Col md={5} xs={4}>
                          <label className="fs-16 fw-500 text-dark">Supplier group:</label>
                        </Col>
                        <Col md={7} xs={8}>
                          <span className="fw-bold fs-16 fw-600 text-dark">{details.supplierGroup.name}</span>
                        </Col>
                      </Row>
                    )}
                    <Row className="mb-3">
                      <Col md={5} xs={4}>
                        <label className="fs-16 fw-500 text-dark">PO ID:</label>
                      </Col>
                      <Col md={7} xs={8}>
                        <span className="fw-bold fs-16 fw-600 text-dark">
                          {details.refKey}
                        </span>
                      </Col>
                    </Row>
                    <Row className="mb-3">
                      <Col md={5} xs={4}>
                        <label className="fs-16 fw-500 text-dark">
                          Request initiated on:
                        </label>
                      </Col>
                      <Col md={7} xs={8}>
                        <span className="fw-bold fs-16 fw-600 text-dark">
                          {Method.convertDateToDDMMYYYYHHMMAMPMWithSeperator(
                            details._createdAt,
                            "-",
                          )}
                        </span>
                      </Col>
                    </Row>
                    <Row className="mb-3">
                      <Col md={5} xs={4}>
                        <label className="fs-16 fw-500 text-dark">
                          Quantity:
                        </label>
                      </Col>
                      <Col md={7} xs={8}>
                        <span className="fw-bold fs-16 fw-600 text-dark">
                          {details.totalQuantities
                            .map((item: any) => {
                              return Method.formatQuantityDisplay(
                                item.quantityType,
                                item.stockCount,
                              );
                            })
                            .join(", ")}
                        </span>
                      </Col>
                    </Row>
                    <Row className="mb-3">
                      <Col md={5} xs={4}>
                        <label className="fs-16 fw-500 text-dark">
                          Shipment weight:
                        </label>
                      </Col>
                      <Col md={7} xs={8}>
                        <span className="fw-bold fs-16 fw-600 text-dark">
                          {details.totalWeight.toFixed(2)} Kg
                        </span>
                      </Col>
                    </Row>
                    <Row className="mb-3">
                      <Col md={5} xs={4}>
                        <label className="fs-16 fw-500 text-dark">
                          Warehouse:
                        </label>
                      </Col>
                      <Col md={7} xs={8}>
                        <span className="fw-bold fs-16 fw-600 text-dark">
                          {details.warehouse.name}
                        </span>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card className="card-stretch card-bordered">
                  <Card.Header className="bg-light align-items-center">
                    <Card.Title className="fs-22 fw-bolder">
                      Transportation details
                    </Card.Title>
                  </Card.Header>
                  {details.shipment.type === UTradeTransport ? (
                    <Card.Body>
                      <Row className="mb-3">
                        <Col md={5} xs={4}>
                          <label className="fs-16 fw-500 text-dark">
                            Shipping carrier:
                          </label>
                        </Col>
                        <Col md={7} xs={8}>
                          <span className="fw-bold fs-16 fw-600 text-dark">
                            U Trade transport service
                          </span>
                        </Col>
                      </Row>
                      <Row className="mb-3">
                        <Col md={5} xs={4}>
                          <label className="fs-16 fw-500 text-dark">
                            Delivery date:
                          </label>
                        </Col>
                        <Col md={7} xs={8}>
                          <span className="fw-bold fs-16 fw-600 text-dark">
                            {details.shipment.date
                              ? Method.convertDateToFormat(
                                details.shipment.date,
                                "DD/MM/YYYY",
                              )
                              : "-"}
                          </span>
                        </Col>
                      </Row>
                      <Row className="mb-3">
                        <Col md={5} xs={4}>
                          <label className="fs-16 fw-500 text-dark">
                            Delivery address:
                          </label>
                        </Col>
                        <Col md={7} xs={8}>
                          <span className="fw-bold fs-16 fw-600 text-dark">
                            {details.warehouse.address}
                          </span>
                        </Col>
                      </Row>
                    </Card.Body>
                  ) : (
                    <Card.Body>
                      <Row className="mb-3">
                        <Col md={5} xs={4}>
                          <label className="fs-16 fw-500 text-dark">
                            Delivered by:
                          </label>
                        </Col>
                        <Col md={7} xs={8}>
                          <span className="fw-bold fs-16 fw-600 text-dark">
                            Vendor Transport
                          </span>
                        </Col>
                      </Row>
                      <Row className="mb-3">
                        <Col md={5} xs={4}>
                          <label className="fs-16 fw-500 text-dark">
                            Delivery date:
                          </label>
                        </Col>
                        <Col md={7} xs={8}>
                          <span className="fw-bold fs-16 fw-600 text-dark">
                            {Method.convertDateToFormat(
                              details.shipment.date,
                              "DD/MM/YYYY",
                            )}
                          </span>
                        </Col>
                      </Row>
                      <Row className="mb-3">
                        <Col md={5} xs={4}>
                          <label className="fs-16 fw-500 text-dark">
                            Delivery address:
                          </label>
                        </Col>
                        <Col md={7} xs={8}>
                          <span className="fw-bold fs-16 fw-600 text-dark">
                            {details.warehouse.address}
                          </span>
                        </Col>
                      </Row>
                    </Card.Body>
                  )}
                </Card>
              </Col>
            </Row>
            <Card className="border border-r10px mb-7">
              <Card.Body className="px-0 py-3">
                <div className="table-responsive">
                  <table className="table table-row-bordered table-rounded align-middle gs-8 gy-4 mb-0">
                    <thead>
                      <tr className="fs-16 fw-600 align-middle">
                        <th className="col-w-30 text-center">Product name</th>
                        <th className="col-w-10 text-center">Unit price</th>
                        <th className="col-w-10 text-center">Qty & Type</th>
                        <th className="col-w-20 text-center">
                          Missing/Damaged items
                        </th>
                        <th className="col-w-10 text-center">Tax(%)</th>
                        <th className="col-w-10 text-center">TAX</th>
                        <th className="col-w-10 text-center">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {details.variants && details.variants.length ? (
                        <>
                          {details.variants.map(
                            (variantVal: any, index: number) =>
                              variantVal.quantityTypes &&
                                variantVal.quantityTypes.length ? (
                                variantVal.quantityTypes.map(
                                  (item: any, qtIndex: number) => {
                                    const totalAmount =
                                      item.stockCount * item.purchaseAmount;
                                    return (
                                      <tr key={`${index}-${qtIndex}`}>
                                        {/* Product cell: only show on first qtyType row */}
                                        <td>
                                          <div className="d-flex align-items-center">
                                            <div className="symbol symbol-50px border me-3">
                                              <ImageWithFallback
                                                src={variantVal.media?.[0]?.url}
                                                className="object-fit-contain"
                                                alt={variantVal.variantName}
                                              />
                                            </div>
                                            <div className="d-flex flex-column">
                                              <span className="fs-15 fw-600">
                                                {variantVal.variantName?.replace(
                                                  /\s*\)\s*/g,
                                                  ")",
                                                )}
                                              </span>
                                              <span className="fs-15 fw-600 mt-2 text-gray">
                                                Product ID:{" "}
                                                {variantVal.refKey ||
                                                  variantVal.variant?.refKey ||
                                                  "-"}
                                              </span>
                                            </div>
                                          </div>
                                          {/* ) : (
                                        <></>
                                      )} */}
                                        </td>

                                        <td className="text-center">
                                          <div className="d-flex flex-column">
                                            <span
                                              className={
                                                qtIndex === 0
                                                  ? "fw-600 fs-15"
                                                  : "fw-600 fs-15 mt-2"
                                              }
                                            >
                                              TSh{" "}
                                              {Method.getGeneralizedAmount(
                                                item.purchaseAmount ?? 0,
                                              )}
                                            </span>
                                          </div>
                                        </td>

                                        <td className="text-center">
                                          <div className="d-flex flex-column">
                                            {
                                              <span
                                                className={
                                                  qtIndex === 0
                                                    ? "fw-600 fs-15"
                                                    : "fw-600 fs-15 mt-2"
                                                }
                                              >
                                                {Method.formatQuantityDisplay(
                                                  item.type,
                                                  item.stockCount,
                                                )}
                                              </span>
                                            }
                                          </div>
                                        </td>

                                        <td className="text-center">
                                          <div className="d-flex flex-column">
                                            {
                                              <span
                                                className={
                                                  qtIndex === 0
                                                    ? "fw-600 fs-15"
                                                    : "fw-600 fs-15 mt-2"
                                                }
                                              >
                                                {Method.formatQuantityDisplay(
                                                  item.type,
                                                  item?.rejectedStockCount > 0
                                                    ? item?.rejectedStockCount
                                                    : 0,
                                                )}
                                              </span>
                                            }
                                          </div>
                                        </td>

                                        <td className="text-center">
                                          <div className="d-flex flex-column">
                                            <span
                                              className={
                                                qtIndex === 0
                                                  ? "fw-600 fs-15"
                                                  : "fw-600 fs-15 mt-2"
                                              }
                                            >
                                              {variantVal?.productTax ?? 0}%
                                            </span>
                                          </div>
                                        </td>

                                        <td className="text-center">
                                          <div className="d-flex flex-column">
                                            <span
                                              className={
                                                qtIndex === 0
                                                  ? "fw-600 fs-15"
                                                  : "fw-600 fs-15 mt-2"
                                              }
                                            >
                                              TSh{" "}
                                              {Method.getGeneralizedAmount(
                                                Method.calculateTax(
                                                  totalAmount ?? 0,
                                                  item.stockCount,
                                                  item?.purchaseAmount,
                                                  variantVal?.productTax
                                                    ? variantVal
                                                    : { productTax: 0 },
                                                ),
                                              )}
                                            </span>
                                          </div>
                                        </td>

                                        <td className="text-center">
                                          <div className="d-flex flex-column">
                                            <span
                                              className={
                                                qtIndex === 0
                                                  ? "fw-600 fs-15"
                                                  : "fw-600 fs-15 mt-2"
                                              }
                                            >
                                              TSh{" "}
                                              {Method.getGeneralizedAmount(
                                                totalAmount ?? 0,
                                              )}
                                            </span>
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  },
                                )
                              ) : (
                                <tr key={index}>
                                  <td>
                                    <div className="d-flex align-items-center">
                                      <div className="symbol symbol-50px border me-3">
                                        <ImageWithFallback
                                          src={variantVal.media?.[0]?.url}
                                          className="object-fit-contain"
                                          alt={variantVal.variantName}
                                        />
                                      </div>
                                      <div className="d-flex flex-column">
                                        <span className="fs-15 fw-600">
                                          {variantVal.variantName?.replace(
                                            /\s*\)\s*/g,
                                            ")",
                                          )}
                                        </span>
                                        <span className="fs-15 fw-600 mt-2 text-gray">
                                          Product ID:{" "}
                                          {variantVal.refKey ||
                                            variantVal.variant?.refKey ||
                                            "-"}
                                        </span>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="text-center">-</td>
                                  <td className="text-center">-</td>
                                  <td className="text-center">-</td>
                                  <td className="text-center">TSh 0</td>
                                </tr>
                              ),
                          )}

                          <tr>
                            <td colSpan={6} className="fs-16 fw-600 text-end">
                              Tax:
                            </td>
                            <td colSpan={1} className="fs-16 fw-600 text-left">
                              TSh{" "}
                              {Method.getGeneralizedAmount(
                                details.variants
                                  ?.map((variantVal: any) =>
                                    variantVal.quantityTypes?.reduce(
                                      (total: number, item: any) => {
                                        return (
                                          total +
                                          Method.calculateTax(
                                            item.stockCount *
                                            item.purchaseAmount,
                                            item.stockCount,
                                            item.purchaseAmount,
                                            variantVal?.productTax
                                              ? variantVal
                                              : { productTax: 0 },
                                          )
                                        );
                                      },
                                      0,
                                    ),
                                  )
                                  .reduce(
                                    (grandTotal: number, currVal: number) =>
                                      grandTotal + currVal,
                                    0,
                                  ),
                              )}
                            </td>
                          </tr>

                          <tr>
                            <td
                              colSpan={6}
                              className="fs-22 fw-800 text-end text-dark"
                            >
                              Grand Total:
                            </td>
                            <td
                              colSpan={1}
                              className="fs-18 fw-800 text-left text-dark"
                            >
                              TSh{" "}
                              {Method.getGeneralizedAmount(
                                details.variants
                                  ?.map((variantVal: any) =>
                                    variantVal.quantityTypes?.reduce(
                                      (total: number, item: any) => {
                                        const totalAmount =
                                          item.stockCount *
                                          item?.purchaseAmount;
                                        return total + totalAmount;
                                      },
                                      0,
                                    ),
                                  )
                                  .reduce(
                                    (grandTotal: number, currVal: number) =>
                                      grandTotal + currVal,
                                    0,
                                  ),
                              )}
                            </td>
                          </tr>
                        </>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              </Card.Body>
            </Card>

            {/* PO summary: PO value vs all GRNs, quantities per product and per GRN */}
            <InwardPoSummary summary={details?.poSummary} />

            {/* Partial Delivery */}
            {details?.deliveries?.length > 0 &&
              details.deliveries.map(
                (partialDeliveryVal: any, partialDeliveryIndex: any) => {
                  return (
                    <Fragment key={partialDeliveryVal._id}>
                      {partialDeliveryIndex === 0 && <div id="grn-section" />}
                      <Col xs={12} id={`delivery-${partialDeliveryVal.refKey}`}>
                        <Card
                          className="p-0 bg-light mb-7"
                          style={{ border: "1px solid #dcdcdc" }}
                        >
                          <Card.Header
                            className={clsx(
                              "h-77px",
                              partialDeliveryVal.isOpen ? "" : "border-r10px",
                            )}
                            style={{
                              background: partialDeliveryVal.isOpen
                                ? "#d8eef7"
                                : "",
                              borderBottom: partialDeliveryVal.isOpen
                                ? "0px"
                                : "",
                            }}
                          >
                            <Row className="fs-16 fw-600 align-items-center w-100">
                              <Col md className="mb-md-0 mb-5 text-start">
                                <Button
                                  className="btn-flush btn-white w-100 text-start"
                                  variant=""
                                >
                                  <span className="fs-18 fw-bolder text-dark">
                                    {details.status === ShipmentPartialDelivered
                                      ? "Partial Delivery"
                                      : "Complete Delivery"}
                                    /{partialDeliveryVal.refKey} <br />
                                    <span className="fs-16 fw-normal">
                                      {Method.convertDateToDDMMYYYYHHMMAMPM(
                                        partialDeliveryVal?.date,
                                      )}
                                    </span>
                                  </span>
                                </Button>
                              </Col>
                              <Col md="auto">
                                <div className="d-flex flex-row align-items-center">
                                  <span className="badge badge-light-success fs-14 fw-600 text-dark p-3 border-r4px me-5">
                                    Received
                                  </span>
                                  {(Method.hasPermission(
                                    PermissionModule.POManagement,
                                    Permissions.PO_SAVE_ALL,
                                    currentUser,
                                  ) ||
                                    currentUser?.roleAndPermission?.roleType ===
                                    "R0003" ||
                                    currentUser?.roleAndPermission?.roleType ===
                                    "R0012") && (
                                      <Button
                                        variant="success"
                                        size="sm"
                                        className="uw-btn uw-btn-success uw-btn-sm me-4"
                                        onClick={() =>
                                          confirmAndSaveAll(partialDeliveryVal)
                                        }
                                        disabled={
                                          saveLoading[
                                          partialDeliveryVal.refKey
                                          ] ||
                                          priceChangeLoading ||
                                          // Disabled when there is no new selling price ready to save
                                          !Object.values(
                                            updatedSellingPrices[
                                            partialDeliveryVal.refKey
                                            ] || {},
                                          ).some((v) => v)
                                        }
                                      >
                                        {saveLoading[partialDeliveryVal.refKey] ||
                                          priceChangeLoading ? (
                                          <span className="spinner-border spinner-border-sm align-middle"></span>
                                        ) : (
                                          <span className="indicator-label fs-14 fw-bold">
                                            Save All
                                          </span>
                                        )}
                                      </Button>
                                    )}
                                  {Method.hasPermission(
                                    PermissionModule.POManagement,
                                    Permissions.Download,
                                    currentUser,
                                  ) && (
                                      <Button
                                        variant="primary"
                                        size="sm"
                                        className="uw-btn uw-btn-primary uw-btn-sm me-4"
                                        onClick={() =>
                                          handleDownloadGRN(
                                            partialDeliveryVal.refKey,
                                          )
                                        }
                                        disabled={loading || !!downloadGRN}
                                      >
                                        {downloadGRN !==
                                          partialDeliveryVal.refKey && (
                                            <span className="indicator-label fs-16 fw-bold">
                                              Download GRN
                                            </span>
                                          )}
                                        {downloadGRN ===
                                          partialDeliveryVal.refKey && (
                                            <span
                                              className="indicator-progress fs-16 fw-bold"
                                              style={{ display: "block" }}
                                            >
                                              Please wait...
                                              <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                                            </span>
                                          )}
                                      </Button>
                                    )}
                                  <Button
                                    variant="link"
                                    className="btn-flush"
                                    onClick={() =>
                                      handlePartialDeliveryToggle(
                                        partialDeliveryIndex,
                                      )
                                    }
                                  >
                                    <img
                                      src={
                                        partialDeliveryVal.isOpen
                                          ? IMAGES.ArrowUp
                                          : IMAGES.ArrowDown
                                      }
                                      alt=""
                                    />
                                  </Button>
                                </div>
                              </Col>
                            </Row>
                          </Card.Header>

                          {partialDeliveryVal.isOpen && (
                            <Card.Body
                              className="pt-0 px-8 pb-8"
                              style={{
                                background: partialDeliveryVal.isOpen
                                  ? "#d8eef7"
                                  : "",
                              }}
                            >
                              <div
                                className="table-responsive"
                                style={{
                                  border: "1px solid #dcdcdc",
                                  borderRadius: "5px",
                                }}
                              >
                                <table
                                  className="table table-rounded table-row-bordered align-middle gs-9 gy-4 mb-0"
                                  style={{ background: "white" }}
                                >
                                  <thead>
                                    <tr className="fs-16 fw-600 h-60px align-middle">
                                      <th className="min-w-280px">
                                        Product Name
                                      </th>
                                      <th className="min-w-150px text-center">
                                        Selling price
                                      </th>
                                      <th className="min-w-150px text-center">
                                        New Selling price
                                      </th>
                                      <th className="min-w-100px text-center">
                                        Purchase Price
                                      </th>
                                      <th className="min-w-100px text-center">
                                        PO Qty.
                                      </th>
                                      <th className="min-w-100px text-center">
                                        Qty. & Type
                                      </th>
                                      <th className="min-w-100px text-center">
                                        Remaining item
                                      </th>
                                      <th className="min-w-100px text-center">
                                        Missing item
                                      </th>
                                      <th className="min-w-100px text-center">
                                        Damaged item
                                      </th>
                                      <th className="min-w-100px text-center">
                                        Tax(%)
                                      </th>
                                      <th className="min-w-100px text-center">
                                        Total tax amount{" "}
                                      </th>
                                      <th className="min-w-180px text-center">
                                        Total amount
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {partialDeliveryVal?.variants?.map(
                                      (variantItem: any, vIndex: number) => {
                                        const variant = variantItem?.variant;

                                        const qtyTypes =
                                          variantItem?.quantityTypes?.filter(
                                            (qt: any) => qt.stockCount > 0,
                                          );
                                        return qtyTypes && qtyTypes.length ? (
                                          qtyTypes.map(
                                            (item: any, qtIndex: number) => {
                                              const totalAmount =
                                                item.stockCount *
                                                item.purchaseAmount;
                                              const uniqueKey = `price_${vIndex}_${qtIndex}`;
                                              const currentSellingPrice =
                                                item?.sellingAmount &&
                                                  item?.sellingAmount > 0
                                                  ? item?.sellingAmount
                                                  : item?.amount;
                                              const currentInputValue =
                                                updatedSellingPrices[
                                                  partialDeliveryVal.refKey
                                                ]?.[uniqueKey] !== undefined
                                                  ? updatedSellingPrices[
                                                  partialDeliveryVal.refKey
                                                  ][uniqueKey]
                                                  : item.sellingAmount > 0
                                                    ? item.sellingAmount.toString()
                                                    : "";
                                              // Active price catalog entry for this variant/quantityType/warehouse drives the "New selling price" input
                                              const catalogKey = `${variant?._id || variant
                                                }_${item.type}`;
                                              const isSellingPriceLoading =
                                                sellingPriceLoading[
                                                variant?._id || variant
                                                ];
                                              const alreadyHasPrice =
                                                Boolean(item?.priceUpdated) ||
                                                Number(
                                                  item?.sellingAmount || 0,
                                                ) > 0;
                                              const hasActiveCatalogPrice =
                                                !alreadyHasPrice &&
                                                activeSellingPrices[
                                                catalogKey
                                                ] !== undefined;
                                              const pendingCatalogValue =
                                                updatedSellingPrices[
                                                partialDeliveryVal.refKey
                                                ]?.[uniqueKey];
                                              const showContactAdminMessage =
                                                !alreadyHasPrice &&
                                                !hasActiveCatalogPrice &&
                                                !isSellingPriceLoading;
                                              const newSellingPriceValue =
                                                alreadyHasPrice
                                                  ? item.sellingAmount.toString()
                                                  : pendingCatalogValue ?? "";
                                              const hasNewSellingPrice =
                                                Number(
                                                  item?.sellingAmount || 0,
                                                ) > 0 ||
                                                Boolean(item?.priceUpdated);
                                              const showLowerPriceWarning =
                                                currentInputValue !== "" &&
                                                !Number.isNaN(
                                                  Number(currentInputValue),
                                                ) &&
                                                Number(currentInputValue) <
                                                Number(
                                                  currentSellingPrice || 0,
                                                );
                                              return (
                                                <tr
                                                  className="fs-15 fw-500"
                                                  key={`${variant?._id || variant
                                                    }-${vIndex}-${qtIndex}`}
                                                >
                                                  <td>
                                                    <div className="d-flex align-items-center">
                                                      <div className="symbol symbol-50px border me-3">
                                                        <ImageWithFallback
                                                          src={
                                                            variantItem
                                                              ?.media?.[0]?.url
                                                          }
                                                          className="object-fit-contain"
                                                          alt={
                                                            variantItem?.variantName
                                                          }
                                                        />
                                                      </div>

                                                      <div className="d-flex flex-column">
                                                        <span className="fs-15 fw-600">
                                                          {variantItem?.variantName?.replace(
                                                            /\s*\)\s*/g,
                                                            ")",
                                                          )}
                                                        </span>

                                                        <span className="fs-15 fw-600 mt-2 text-gray">
                                                          Product ID:{" "}
                                                          {variantItem?.refKey ||
                                                            variantItem?.variant
                                                              ?.refKey ||
                                                            "-"}
                                                        </span>
                                                      </div>
                                                    </div>
                                                    {/* ) : (
                                                    <></>
                                                  )} */}
                                                  </td>
                                                  <td className="text-center">
                                                    <div className="d-flex justify-content-center border border-gray-500 rounded-2 px-2 py-2 align-items-center bg-light">
                                                      <span
                                                        key={item.type}
                                                        className="fw-600 fs-15"
                                                      >
                                                        TSh{" "}
                                                        {Method.getGeneralizedAmount(
                                                          item?.sellingAmount &&
                                                            item?.sellingAmount >
                                                            0
                                                            ? item?.sellingAmount
                                                            : item?.amount,
                                                        )}
                                                      </span>
                                                    </div>
                                                  </td>

                                                  <td className="text-center">
                                                    {showContactAdminMessage ? (
                                                      <span className="fs-13 fw-600 text-danger">
                                                        Contact Admin to update
                                                        the selling price
                                                      </span>
                                                    ) : (
                                                      <div className="d-flex justify-content-between border border-primary rounded-2 px-2 py-1 align-items-center">
                                                        <Form.Control
                                                          type="text"
                                                          className="form-control-transparent fs-15 p-0 text-center text-dark fw-bolder"
                                                          style={{
                                                            backgroundColor:
                                                              "transparent",
                                                            opacity: 1,
                                                            width: "100%",
                                                            minWidth: 150,
                                                          }}
                                                          placeholder={
                                                            isSellingPriceLoading
                                                              ? "Loading..."
                                                              : "Enter Price"
                                                          }
                                                          value={
                                                            `${newSellingPriceValue}  GP(${calculateGPMargin(item.purchaseAmount ?? 0, newSellingPriceValue)})`
                                                          }
                                                          onChange={(e) =>
                                                            handlePriceChange(
                                                              partialDeliveryVal.refKey,
                                                              variantItem,
                                                              item,
                                                              e.target.value,
                                                              vIndex,
                                                              qtIndex,
                                                            )
                                                          }
                                                          disabled
                                                        />
                                                        {!alreadyHasPrice &&
                                                          pendingCatalogValue &&
                                                          (currentUser?.userType ===
                                                            Admin ||
                                                            currentUser
                                                              ?.roleAndPermission
                                                              ?.roleType ===
                                                            "R0003" ||
                                                            currentUser
                                                              ?.roleAndPermission
                                                              ?.roleType ===
                                                            "R0012") && (
                                                            <div
                                                              className="ms-2 d-flex align-items-center justify-content-center"
                                                              onClick={() => {
                                                                if (
                                                                  !saveSingleLoading[
                                                                  `${partialDeliveryVal.refKey}_${vIndex}_${qtIndex}`
                                                                  ] &&
                                                                  !priceChangeLoading
                                                                ) {
                                                                  confirmAndSaveSingle(
                                                                    partialDeliveryVal,
                                                                    vIndex,
                                                                    qtIndex,
                                                                    variant?._id ||
                                                                    variant,
                                                                    item.type,
                                                                    pendingCatalogValue,
                                                                  );
                                                                }
                                                              }}
                                                              style={{
                                                                cursor:
                                                                  !saveSingleLoading[
                                                                    `${partialDeliveryVal.refKey}_${vIndex}_${qtIndex}`
                                                                  ]
                                                                    ? "pointer"
                                                                    : "default",
                                                                opacity:
                                                                  !saveSingleLoading[
                                                                    `${partialDeliveryVal.refKey}_${vIndex}_${qtIndex}`
                                                                  ]
                                                                    ? 1
                                                                    : 0.5,
                                                              }}
                                                            >
                                                              {saveSingleLoading[
                                                                `${partialDeliveryVal.refKey}_${vIndex}_${qtIndex}`
                                                              ] ? (
                                                                <span className="spinner-border spinner-border-sm text-primary align-middle"></span>
                                                              ) : (
                                                                <img
                                                                  src={
                                                                    GreenTickIcon
                                                                  }
                                                                  alt="Save"
                                                                  width={20}
                                                                  height={20}
                                                                />
                                                              )}
                                                            </div>
                                                          )}
                                                        {savedThisSession.has(
                                                          `${partialDeliveryVal.refKey}_${vIndex}_${qtIndex}`,
                                                        ) && (
                                                            <div className="ms-2 d-flex align-items-center justify-content-center">
                                                              <img
                                                                src={
                                                                  GreenTickIcon
                                                                }
                                                                alt="Saved"
                                                                width={20}
                                                                height={20}
                                                              />
                                                            </div>
                                                          )}
                                                      </div>
                                                    )}
                                                  </td>

                                                  <td className="text-center">
                                                    <span
                                                      className={
                                                        qtIndex === 0
                                                          ? "fw-600 fs-15"
                                                          : "fw-600 fs-15 mt-2"
                                                      }
                                                    >
                                                      TSh{" "}
                                                      {Method.getGeneralizedAmount(
                                                        item.purchaseAmount ??
                                                        0,
                                                      )}
                                                    </span>
                                                  </td>

                                                  {/* PO Quantity column */}
                                                  <td className="text-center">
                                                    <span
                                                      className={
                                                        qtIndex === 0
                                                          ? "fw-600 fs-15"
                                                          : "fw-600 fs-15 mt-2"
                                                      }
                                                    >
                                                      {(() => {
                                                        // Find the variant in details.variants by _id
                                                        const poVariant =
                                                          details?.variants?.find(
                                                            (v: any) =>
                                                              v.variant?._id ===
                                                              variant?._id,
                                                          );
                                                        // Find the quantityType by type
                                                        const poQtyType =
                                                          poVariant?.quantityTypes?.find(
                                                            (qt: any) =>
                                                              qt.type ===
                                                              item.type,
                                                          );
                                                        return poQtyType
                                                          ? Method.formatQuantityDisplay(
                                                            poQtyType.type,
                                                            poQtyType.stockCount,
                                                          )
                                                          : "-";
                                                      })()}
                                                    </span>
                                                  </td>

                                                  <td className="text-center">
                                                    {
                                                      <span
                                                        className={
                                                          qtIndex === 0
                                                            ? "fw-600 fs-15"
                                                            : "fw-600 fs-15 mt-2"
                                                        }
                                                      >
                                                        {Method.formatQuantityDisplay(
                                                          item.type,
                                                          item.stockCount,
                                                        )}
                                                      </span>
                                                    }
                                                  </td>

                                                  <td className="text-center">
                                                    <div className="d-flex flex-column">
                                                      <span
                                                        className={
                                                          qtIndex === 0
                                                            ? "fw-600 fs-15"
                                                            : "fw-600 fs-15 mt-2"
                                                        }
                                                      >
                                                        {(() => {
                                                          if (
                                                            item?.reason ===
                                                            1 ||
                                                            item?.reason === 2
                                                          ) {
                                                            return Method.formatQuantityDisplay(
                                                              item.type,
                                                              0,
                                                            );
                                                          }

                                                          const poVariant =
                                                            details?.variants?.find(
                                                              (v: any) =>
                                                                v.variant
                                                                  ?._id ===
                                                                variant?._id,
                                                            );
                                                          const poQtyType =
                                                            poVariant?.quantityTypes?.find(
                                                              (qt: any) =>
                                                                qt.type ===
                                                                item.type,
                                                            );
                                                          const cumulativeStock =
                                                            details.deliveries
                                                              .slice(
                                                                0,
                                                                partialDeliveryIndex +
                                                                1,
                                                              )
                                                              .reduce(
                                                                (
                                                                  sum: number,
                                                                  delivery: any,
                                                                ) => {
                                                                  const dVariant =
                                                                    delivery.variants?.find(
                                                                      (
                                                                        v: any,
                                                                      ) =>
                                                                        v
                                                                          .variant
                                                                          ?._id ===
                                                                        variant?._id,
                                                                    );
                                                                  const dQtyType =
                                                                    dVariant?.quantityTypes?.find(
                                                                      (
                                                                        qt: any,
                                                                      ) =>
                                                                        qt.type ===
                                                                        item.type,
                                                                    );
                                                                  return (
                                                                    sum +
                                                                    (dQtyType?.stockCount ||
                                                                      0)
                                                                  );
                                                                },
                                                                0,
                                                              );
                                                          const remaining =
                                                            (poQtyType?.stockCount ||
                                                              0) -
                                                            cumulativeStock;
                                                          return Method.formatQuantityDisplay(
                                                            item.type,
                                                            remaining > 0
                                                              ? remaining
                                                              : 0,
                                                          );
                                                        })()}
                                                      </span>
                                                    </div>
                                                  </td>

                                                  <td className="text-center">
                                                    <div className="d-flex flex-column">
                                                      {
                                                        <span
                                                          className={
                                                            qtIndex === 0
                                                              ? "fw-600 fs-15"
                                                              : "fw-600 fs-15 mt-2"
                                                          }
                                                        >
                                                          {Method.formatQuantityDisplay(
                                                            item.type,
                                                            item?.reason === 1
                                                              ? item?.rejectedStockCount
                                                              : 0,
                                                          )}
                                                        </span>
                                                      }
                                                    </div>
                                                  </td>
                                                  <td className="text-center">
                                                    <div className="d-flex flex-column">
                                                      {
                                                        <span
                                                          className={
                                                            qtIndex === 0
                                                              ? "fw-600 fs-15"
                                                              : "fw-600 fs-15 mt-2"
                                                          }
                                                        >
                                                          {Method.formatQuantityDisplay(
                                                            item.type,
                                                            item?.reason === 2
                                                              ? item?.rejectedStockCount
                                                              : 0,
                                                          )}
                                                        </span>
                                                      }
                                                    </div>
                                                  </td>

                                                  <td className="text-center">
                                                    <div className="d-flex flex-column">
                                                      <span
                                                        className={
                                                          qtIndex === 0
                                                            ? "fw-600 fs-15"
                                                            : "fw-600 fs-15 mt-2"
                                                        }
                                                      >
                                                        {variantItem?.productTax ??
                                                          0}
                                                        %
                                                      </span>
                                                    </div>
                                                  </td>

                                                  <td className="text-center">
                                                    <div className="d-flex flex-column">
                                                      <span
                                                        className={
                                                          qtIndex === 0
                                                            ? "fw-600 fs-15"
                                                            : "fw-600 fs-15 mt-2"
                                                        }
                                                      >
                                                        TSh{" "}
                                                        {Method.getGeneralizedAmount(
                                                          Method.calculateTax(
                                                            totalAmount ?? 0,
                                                            item?.stockCount,
                                                            item?.purchaseAmount,
                                                            variantItem?.productTax
                                                              ? variantItem
                                                              : {
                                                                productTax: 0,
                                                              },
                                                          ),
                                                        )}
                                                      </span>
                                                    </div>
                                                  </td>

                                                  <td className="text-center">
                                                    <span
                                                      className={
                                                        qtIndex === 0
                                                          ? "fw-600 fs-15"
                                                          : "fw-600 fs-15 mt-2"
                                                      }
                                                    >
                                                      TSh{" "}
                                                      {Method.getGeneralizedAmount(
                                                        totalAmount ?? 0,
                                                      )}
                                                    </span>
                                                  </td>
                                                </tr>
                                              );
                                            },
                                          )
                                        ) : (
                                          <tr key={variant?._id || variant}>
                                            <td>
                                              <div className="d-flex align-items-center">
                                                <div className="symbol symbol-50px border me-3">
                                                  <ImageWithFallback
                                                    src={
                                                      variantItem?.media?.[0]
                                                        ?.url
                                                    }
                                                    className="object-fit-contain"
                                                    alt={
                                                      variantItem?.variantName
                                                    }
                                                  />
                                                </div>

                                                <div className="d-flex flex-column">
                                                  <span className="fs-15 fw-600">
                                                    {variantItem?.variantName?.replace(
                                                      /\s*\)\s*/g,
                                                      ")",
                                                    )}
                                                  </span>

                                                  <span className="fs-15 fw-600 mt-2 text-gray">
                                                    Product ID:{" "}
                                                    {variantItem?.refKey ||
                                                      variantItem?.variant
                                                        ?.refKey ||
                                                      "-"}
                                                  </span>
                                                </div>
                                              </div>
                                            </td>
                                            <td className="text-center">-</td>
                                            <td className="text-center">-</td>
                                            <td className="text-center">-</td>
                                            <td className="text-center">-</td>
                                            <td className="text-center">-</td>
                                            <td className="text-center">-</td>
                                            <td className="text-center">
                                              TSh 0
                                            </td>
                                          </tr>
                                        );
                                      },
                                    )}
                                    <tr>
                                      <td
                                        colSpan={11}
                                        className="fs-16 fw-600 text-end"
                                      >
                                        Tax:
                                      </td>
                                      <td
                                        colSpan={1}
                                        className="fs-16 fw-600 text-left"
                                      >
                                        TSh{" "}
                                        {Method.getGeneralizedAmount(
                                          partialDeliveryVal?.variants
                                            ?.map((variantVal: any) =>
                                              variantVal.quantityTypes?.reduce(
                                                (total: number, item: any) => {
                                                  return (
                                                    total +
                                                    Method.calculateTax(
                                                      item.stockCount *
                                                      item.purchaseAmount,
                                                      item.stockCount,
                                                      item.purchaseAmount,
                                                      variantVal?.productTax
                                                        ? variantVal
                                                        : { productTax: 0 },
                                                    )
                                                  );
                                                },
                                                0,
                                              ),
                                            )
                                            .reduce(
                                              (
                                                grandTotal: number,
                                                currVal: number,
                                              ) => grandTotal + currVal,
                                              0,
                                            ),
                                        )}
                                      </td>
                                    </tr>
                                    <tr>
                                      <td
                                        colSpan={11}
                                        className="fs-22 fw-800 text-end text-dark"
                                      >
                                        Grand Total:
                                      </td>
                                      <td
                                        colSpan={1}
                                        className="fs-18 fw-800 text-left text-dark"
                                      >
                                        TSh{" "}
                                        {Method.getGeneralizedAmount(
                                          partialDeliveryVal?.variants
                                            ?.map((variantVal: any) =>
                                              variantVal.quantityTypes?.reduce(
                                                (total: number, item: any) => {
                                                  const totalAmount =
                                                    item.stockCount *
                                                    item.purchaseAmount;
                                                  return total + totalAmount;
                                                },
                                                0,
                                              ),
                                            )
                                            .reduce(
                                              (
                                                grandTotal: number,
                                                currVal: number,
                                              ) => grandTotal + currVal,
                                              0,
                                            ),
                                        )}
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>

                              {/* Delivery Details */}
                              <Row className="d-flex align-items-center justify-content-between mb-7">
                                <Col md className="mb-md-0 mb-5 text-start">
                                  <h1 className="fs-18 fw-bolder mb-0 mt-6">
                                    Delivery Details
                                  </h1>
                                </Col>
                              </Row>
                              <div className="table-responsive bg-white p-7 px-10 border-r10px">
                                <div className="d-flex mb-3">
                                  <span className="min-w-200px fs-16 fw-600 text-gray-700">
                                    Invoice Number:
                                  </span>
                                  <span className="fw-bolder fs-16">
                                    {partialDeliveryVal?.invoiceNumber}
                                  </span>
                                </div>
                                <div className="d-flex mb-3">
                                  <span className="min-w-200px fs-16 fw-600 text-gray-700">
                                    Invoice Amount:
                                  </span>
                                  <span className="fw-bolder fs-16">
                                    TSh{" "}
                                    {Method.getGeneralizedAmount(
                                      partialDeliveryVal.variants
                                        ?.map((variantVal: any) =>
                                          variantVal.quantityTypes?.reduce(
                                            (total: number, item: any) => {
                                              const totalAmount =
                                                item.stockCount *
                                                item?.purchaseAmount;
                                              return total + totalAmount;
                                            },
                                            0,
                                          ),
                                        )
                                        .reduce(
                                          (
                                            grandTotal: number,
                                            currVal: number,
                                          ) => grandTotal + currVal,
                                          0,
                                        ),
                                    )}
                                  </span>
                                </div>
                                <div className="d-flex mb-3">
                                  <span className="min-w-200px fs-16 fw-600 text-gray-700">
                                    Delivery Date:
                                  </span>
                                  <span className="fw-bolder fs-16">
                                    {Method.convertDateToDDMMYYYY(
                                      partialDeliveryVal?.date,
                                    )}
                                  </span>
                                </div>
                                <div className="d-flex mb-3">
                                  <span className="min-w-200px fs-16 fw-600 text-gray-700">
                                    Driver Name:
                                  </span>
                                  <span className="fw-bolder fs-16">
                                    {partialDeliveryVal?.driverName}
                                  </span>
                                </div>
                                <div className="d-flex mb-3">
                                  <span className="min-w-200px fs-16 fw-600 text-gray-700">
                                    Contact Number:
                                  </span>
                                  <span className="fw-bolder fs-16">
                                    {partialDeliveryVal?.driverPhone}
                                  </span>
                                </div>
                                <div className="d-flex mb-3">
                                  <span className="min-w-200px fs-16 fw-600 text-gray-700">
                                    Vehicle Number:
                                  </span>
                                  <span className="fw-bolder fs-16">
                                    {partialDeliveryVal?.vehicleNumber}
                                  </span>
                                </div>
                                {partialDeliveryVal?.receiptNumber && (
                                  <div className="d-flex mb-3">
                                    <span className="min-w-200px fs-16 fw-600 text-gray-700">
                                      Receipt Number:
                                    </span>
                                    <span className="fw-bolder fs-16">
                                      {partialDeliveryVal?.receiptNumber}
                                    </span>
                                  </div>
                                )}
                                {partialDeliveryVal?.receiptVerificationCode && (
                                  <div className="d-flex mb-3">
                                    <span className="min-w-200px fs-16 fw-600 text-gray-700">
                                      Receipt Verification Code:
                                    </span>
                                    <span className="fw-bolder fs-16">
                                      {
                                        partialDeliveryVal?.receiptVerificationCode
                                      }
                                    </span>
                                  </div>
                                )}
                                {partialDeliveryVal?.receiptDate && (
                                  <div className="d-flex mb-3">
                                    <span className="min-w-200px fs-16 fw-600 text-gray-700">
                                      Receipt Date:
                                    </span>
                                    <span className="fw-bolder fs-16">
                                      {Method.convertDateToDDMMYYYYHHMMAMPM(
                                        partialDeliveryVal?.receiptDate,
                                      )}
                                    </span>
                                  </div>
                                )}
                              </div>

                              {/* Attached Documents */}
                              {partialDeliveryVal?.images &&
                                partialDeliveryVal.images.length > 0 && (
                                  <>
                                    <Row className="d-flex align-items-center justify-content-between mb-7">
                                      <Col
                                        md
                                        className="mb-md-0 mb-5 text-start"
                                      >
                                        <h1 className="fs-18 fw-bolder mb-0 mt-6">
                                          Attached Documents
                                        </h1>
                                      </Col>
                                    </Row>
                                    <div className="d-flex flex-row flex-wrap gap-5">
                                      {partialDeliveryVal.images.map(
                                        (img: string, imgIdx: number) => (
                                          <div
                                            className="d-flex flex-column"
                                            key={imgIdx}
                                          >
                                            <a
                                              href={img}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                            >
                                              <ImageWithFallback
                                                src={img}
                                                alt={`Attached document ${imgIdx + 1
                                                  }`}
                                                height={250}
                                                width={250}
                                                style={{
                                                  borderRadius: "7px",
                                                  objectFit: "cover",
                                                }}
                                              />
                                            </a>
                                            <span className="fw-normal text-center fs-16 w-250px mt-3">
                                              {Method.convertDateToDDMMYYYYHHMMAMPM(
                                                partialDeliveryVal?.date,
                                              )}
                                            </span>
                                          </div>
                                        ),
                                      )}
                                    </div>
                                  </>
                                )}
                              {/* Inward GRN: Warehouse photos + Finance documents */}
                              <InwardDeliveryFiles
                                files={partialDeliveryVal?.inwardFiles}
                              />
                            </Card.Body>
                          )}
                        </Card>
                      </Col>
                    </Fragment>
                  );
                },
              )}
          </>
        )}
        <PoTimelinePanel poId={details?._id || state?.id} warehouse={details?.warehouse?.reference} />
      </div>
    </>
  );
};

export default PurchaseOrderDetailsV2;
